'use client'

import React, { useEffect, useRef, useState } from "react";
import styles from "./GalleryWithInfo.module.scss";

export interface GalleryItem {
  imageUrl?: string;
  eyebrow: string;
  title: string;
  description: string;
  buttonText?: string;
  buttonLink?: string;
}

interface GalleryWithInfoProps {
  items: GalleryItem[];
  imageGap?: string;
  cardBackgroundColor?: string;
  buttonColor?: string;
  hoverEffect?: boolean;
  showButton?: boolean;
}

const AnimatedCard: React.FC<{
  item: GalleryItem;
  idx: number;
  buttonColor: string;
  showButton: boolean;
  isActive: boolean;
}> = ({ item, idx, buttonColor, showButton, isActive }) => {
  return (
    <div
      className={`${styles.card} ${isActive ? styles.activeCard : ""}`}
      style={{ animationDelay: `${0.2 * idx}s` }}
    >
      <div className={styles.eyebrow}>{item.eyebrow}</div>
      <h4 data-aos='fade-right'>{item.title}</h4>
      {item.description.split('\n').map((line, idx) => (
        <span key={idx}>
          {line}
          <br />
        </span>
      ))}
      {showButton && item.buttonText && item.buttonLink && (
        <a
          href={item.buttonLink}
          className={styles.button}
          style={{ backgroundColor: buttonColor }}
          data-aos='fade-up'
        >
          {item.buttonText}
        </a>
      )}
      {idx < 2 && <div className={styles.divider} />}
    </div>
  );
};

export const GalleryWithInfo: React.FC<GalleryWithInfoProps> = ({
  items,
  imageGap = "2rem",
  cardBackgroundColor = "#000000",
  buttonColor = "#111",
  hoverEffect = true,
  showButton = true,
}) => {
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleIndex, setVisibleIndex] = useState<number>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const sorted = [...entries]
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (sorted.length > 0) {
          const index = imageRefs.current.findIndex((ref) => ref === sorted[0].target);
          if (index !== -1) {
            setVisibleIndex(index);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: Array.from({ length: 100 }, (_, i) => i / 100), // 0.01 to 0.99
      }
    );

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      imageRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.grid}>
        <div className={styles.imageColumn} style={{ gap: imageGap }}>
          {items.map((item, idx) => (
            <div
              key={idx}
              // @ts-expect-error
              ref={(el) => (imageRefs.current[idx] = el)}
              className={`${styles.imageWrapper} ${!hoverEffect ? styles.noHover : ""}`}
              data-aos='fade'
            >
              {item.imageUrl && (
                <img src={item.imageUrl} alt={`gallery-${idx}`} className={styles.image} />
              )}            </div>
          ))}
        </div>
        <div className={styles.cardsColumn}>
          <div className={styles.cardsWrapper}>
            <div className={styles.cards} style={{ backgroundColor: cardBackgroundColor }}>
              {items.map((item, idx) => (
                <AnimatedCard
                  key={idx}
                  item={item}
                  idx={idx}
                  buttonColor={buttonColor}
                  showButton={showButton}
                  isActive={visibleIndex === idx}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
